import { CardNumberElement, CardExpiryElement, CardCvcElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { useState } from 'react'
import { styled } from 'styles'
import { StripeLabel } from 'features/incidents/components/style'
import { ErrorBox } from 'features/profile/components/EditProfileForm'
import { Button } from 'components/button'
import { Box, Flex } from 'components/elements'
import { useNavigate } from 'react-router'
import { routes } from 'configs/constants'
import { StripeInputProps } from 'features/quote/components/type'
import { StripePaymentStatus } from 'features/incidents/components/enums'
import { buttonCss } from 'features/quote/components/GetAQuoteProcess/CoveragePlan'
import { CircularProgress } from '@mui/material'

export const customInputStyle = {
  base: {
    'fontSize': '16px',
    'fontWeight': '700',
    'lineHeight': '24px',
    'color': 'white',
    '::placeholder': {
      color: '#A1A1A1',
    },
  },

  invalid: {
    color: '#FF0303',
  },
}

const StripePayment = (props: StripeInputProps) => {
  const { onSubmit, isLoading, IsOpenInModal, handleCancel, isChangedCard } = props
  const stripe = useStripe()
  const elements = useElements()
  const [submitError, setSubmitError] = useState('')
  const [isStripeLoading, setIsStripeLoading] = useState(false)
  //Stripe Error handling
  const [fieldErrors, setFieldErrors] = useState({
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
  })

  //
  /**
   * Handles the change of a field in the Stripe payment form.
   * Updates the error messages in the state.
   *
   * @param {string} field - The name of the field that changed.
   * @param {object} event - The event that triggered the change.
   * @param {object} event.error - The error object that contains the message.
   */
  const handleFieldChange = (field: string, event: { error?: { message: string } }) => {
    if (event.error) {
      // If there is an error, update the state with the error message
      setFieldErrors(prev => ({
        ...prev,
        [field]: event?.error?.message,
      }))
    } else {
      // If there is no error, clear the error message from the state
      setFieldErrors(prev => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  const navigate = useNavigate()

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault()

    if (!stripe || !elements) {
      setSubmitError('Stripe.js or Elements is not available')
      return
    }
    // Ensure CardNumberElement, CardExpiryElement, and CardCvcElement are mounted
    const cardNumberElement = elements.getElement(CardNumberElement)
    const cardExpiryElement = elements.getElement(CardExpiryElement)
    const cardCvcElement = elements.getElement(CardCvcElement)
    if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
      setSubmitError('One or more card elements are not mounted')
      return
    }
    setIsStripeLoading(true)

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardNumberElement,
    })

    if (error && error.message) {
      setIsStripeLoading(false)
      setSubmitError(error.message)
    } else {
      setIsStripeLoading(false)
      // Explicitly type paymentMethod to avoid 'never' type
      const typedPaymentMethod = paymentMethod as { id?: string }

      if (typedPaymentMethod && typedPaymentMethod.id) {
        onSubmit(typedPaymentMethod.id)
      } else {
        // paymentMethod is undefined or doesn't have an 'id' property
        setSubmitError('Invalid payment method')
      }
    }
  }
  return (
    <>
      <StripeBox>
        {!!submitError && <StripeErrorBox>{submitError}</StripeErrorBox>}

        <StripeLabel className="Label">Card Number</StripeLabel>

        <CardNumberElement
          className={` Card CardBorder bg-succes`}
          options={{ style: customInputStyle, showIcon: true }}
          onChange={event => handleFieldChange('cardNumber', event)}
        />

        {fieldErrors.cardNumber && <StripeErrorBox>{fieldErrors.cardNumber}</StripeErrorBox>}
      </StripeBox>
      <StripeBox>
        <StripeLabel className="Label">Expiry Date</StripeLabel>
        <CardExpiryElement className={` Card CardBorder bg-succes`} options={{ style: customInputStyle }} onChange={event => handleFieldChange('cardExpiry', event)} />
        {fieldErrors.cardExpiry && <StripeErrorBox>{fieldErrors.cardExpiry}</StripeErrorBox>}
      </StripeBox>
      <StripeBox>
        <StripeLabel className="Label">CVC</StripeLabel>
        <CardCvcElement className={` Card CardBorder bg-succes`} options={{ style: customInputStyle }} onChange={event => handleFieldChange('cardCvc', event)} />
        {fieldErrors.cardCvc && <StripeErrorBox>{fieldErrors.cardCvc}</StripeErrorBox>}
      </StripeBox>

      {IsOpenInModal ? (
        <Flex justify={'center'} wrap={'wrap'} css={{ gap: 28, mt: '$5' }}>
          <Button size="sm" bordered={true} type="button" onClick={handleCancel}>
            Cancel
          </Button>
          {isChangedCard ? (
            <Button size="sm" type="button" onClick={handleSubmit} disabled={!stripe || isLoading || isStripeLoading}>
              {isLoading || isStripeLoading ? <CircularProgress size={18} sx={{ color: 'var(--colors-white) !important' }} /> : StripePaymentStatus.CHANGE}
            </Button>
          ) : (
            <Button size="sm" type="button" onClick={handleSubmit} disabled={!stripe || isLoading || isStripeLoading}>
              {isLoading || isStripeLoading ? <CircularProgress size={18} sx={{ color: 'var(--colors-white) !important' }} /> : StripePaymentStatus.PAY}
            </Button>
          )}
        </Flex>
      ) : (
        <Box css={{ ...buttonCss, mt: '$5' }}>
          <Button bordered={true} type="button" onClick={() => navigate(routes.login)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!stripe || isLoading || isStripeLoading}>
            {isLoading || isStripeLoading ? <CircularProgress size={18} sx={{ color: 'var(--colors-white) !important' }} /> : StripePaymentStatus.PAY}
          </Button>
        </Box>
      )}
    </>
  )
}
export const StripeBox = styled('div', {
  '.Card': {
    padding: '0.75rem 1rem',
    width: '100%',
  },
  '.CardBorder': {
    'background': '$white1A',
    'borderRadius': '4px',
    '&.bg-succes': {
      border: '1px solid $border',
    },
    '&.bg-error': {
      border: '1px solid $error',
    },
  },
  '.Label': {
    marginBottom: '$2',
  },
})
const StripeErrorBox = styled(ErrorBox, {
  textAlign: 'left',
  mb: '$2',
})

export default StripePayment
